import { useState, useRef } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

export function InputOtpRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()

  const inputChildren = (element.children ?? []).filter((c) => c.type === "input")
  const digitCount = inputChildren.length || 6
  const [digits, setDigits] = useState<string[]>(Array(digitCount).fill(""))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1)
    }
    if (value && !/^\d$/.test(value)) return

    const newDigits = [...digits]
    newDigits[index] = value
    setDigits(newDigits)

    if (value && index < digitCount - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    const otp = newDigits.join("")
    dispatchEvent(element.id, "change", { value: otp })
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const midpoint = Math.floor(digitCount / 2)

  return (
    <div
      className={cn("flex items-center gap-2", element.className)}
      data-testid={`input-otp-${element.id}`}
      role="group"
      aria-label={element.ariaLabel ?? "One-time password input"}
    >
      {Array.from({ length: digitCount }, (_, i) => (
        <span key={i} className="flex items-center gap-2">
          {i === midpoint && (
            <span className="w-4 text-center text-lg text-muted-foreground">-</span>
          )}
          <input
            ref={(el) => { inputRefs.current[i] = el }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digits[i]}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="flex h-12 w-10 items-center justify-center rounded-md border border-input bg-background text-center text-lg font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={inputChildren[i]?.ariaLabel ?? `Digit ${i + 1}`}
          />
        </span>
      ))}
    </div>
  )
}
